import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Star, GitFork, Users, TrendingUp, Award, Filter, ChevronDown } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';

const CATEGORIES = ['All', 'Creative', 'Technical', 'Business', 'Legal', 'Science', 'Philosophy', 'Education', 'Healthcare', 'Finance'];
const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'newest', label: 'Newest' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'trending', label: 'Trending' },
];

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function Community() {
  const { user } = useAuth() || {};
  const [activeTab, setActiveTab] = useState('personas');
  const [personas, setPersonas] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [sortBy, setSortBy] = useState('popular');
  const [sortOpen, setSortOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      API.get('/api/personas').then(r => setPersonas(r.data.personas || r.data || [])).catch(() => setPersonas([])),
      API.get('/api/playbooks').then(r => setPlaybooks(r.data.playbooks || [])).catch(() => setPlaybooks([])),
    ]).finally(() => setLoading(false));
  }, []);

  const items = activeTab === 'personas' ? personas : playbooks;

  const filtered = useMemo(() => {
    let result = items.filter(item => {
      const matchSearch = !debouncedSearch ||
        item.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        item.description?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        (item.tags || []).some(t => t.toLowerCase().includes(debouncedSearch.toLowerCase()));
      const matchCategory = category === 'All' ||
        item.domain?.toLowerCase() === category.toLowerCase() ||
        item.category?.toLowerCase() === category.toLowerCase();
      return matchSearch && matchCategory;
    });

    result.sort((a, b) => {
      if (sortBy === 'popular') return (b.use_count || 0) - (a.use_count || 0);
      if (sortBy === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      if (sortBy === 'rating') return (b.avg_rating || 0) - (a.avg_rating || 0);
      if (sortBy === 'trending') return (b.trending_score || b.use_count || 0) - (a.trending_score || a.use_count || 0);
      return 0;
    });

    return result;
  }, [items, debouncedSearch, category, sortBy]);

  const featured = useMemo(() =>
    items.filter(i => i.featured || i.staff_pick).slice(0, 6),
    [items]
  );

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold font-['Barlow_Condensed']">Community Marketplace</h1>
          <p className="text-sm text-[#A0A0A0] mt-1">Discover and fork personas and playbooks from the community</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-[#2A2A2A]" data-testid="community-tabs">
          {['personas', 'playbooks'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 min-h-[44px] text-sm font-medium font-['Barlow_Condensed'] capitalize border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-[#FF4D00] text-[#FF4D00]'
                  : 'border-transparent text-[#A0A0A0] hover:text-[#F5F5F5]'
              }`}
              data-testid={`tab-${tab}`}
              aria-selected={activeTab === tab}
              role="tab"
            >
              {tab === 'personas' ? <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" /> : null}
              {tab}
            </button>
          ))}
        </div>

        {/* Featured / Staff Picks */}
        {featured.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-mono text-[#FF4D00] mb-3 flex items-center gap-2">
              <Award className="w-4 h-4" /> FEATURED
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {featured.map(item => (
                <FeaturedCard key={item.slug || item.id} item={item} type={activeTab} />
              ))}
            </div>
          </div>
        )}

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0A0A0]" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${activeTab}...`}
              className="w-full pl-10 pr-4 py-2.5 min-h-[44px] bg-[#1A1A1A] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#FF4D00] placeholder:text-[#555]"
              data-testid="community-search"
            />
          </div>

          {/* Category filter */}
          <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
            {CATEGORIES.slice(0, 6).map(cat => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3 py-2 min-h-[44px] text-xs font-mono whitespace-nowrap border transition-colors ${
                  category === cat
                    ? 'border-[#FF4D00] text-[#FF4D00] bg-[#FF4D00]/10'
                    : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#555]'
                }`}
                data-testid={`filter-${cat}`}
                aria-label={`Filter by ${cat}`}
                aria-pressed={category === cat}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Sort dropdown */}
          <div className="relative">
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="flex items-center gap-2 px-3 py-2.5 min-h-[44px] border border-[#2A2A2A] text-xs font-mono text-[#A0A0A0] hover:text-[#F5F5F5] w-full sm:w-auto"
              data-testid="sort-toggle"
              aria-label={`Sort by: ${SORT_OPTIONS.find(s => s.value === sortBy)?.label}`}
              aria-expanded={sortOpen}
            >
              <Filter className="w-3.5 h-3.5" />
              {SORT_OPTIONS.find(s => s.value === sortBy)?.label}
              <ChevronDown className="w-3 h-3" />
            </button>
            {sortOpen && (
              <div className="absolute right-0 top-full mt-1 bg-[#1A1A1A] border border-[#2A2A2A] z-20 min-w-[160px]">
                {SORT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setSortBy(opt.value); setSortOpen(false); }}
                    className={`w-full text-left px-3 py-2.5 min-h-[44px] text-xs font-mono hover:bg-[#222] ${
                      sortBy === opt.value ? 'text-[#FF4D00]' : 'text-[#A0A0A0]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#A0A0A0] text-sm">No {activeTab} found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="community-grid">
            {filtered.map(item => (
              <ItemCard key={item.slug || item.id} item={item} type={activeTab} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FeaturedCard({ item, type }) {
  const href = type === 'personas' ? `/voices` : `/playbooks/${item.slug}`;
  return (
    <Link to={href} className="block p-4 bg-gradient-to-br from-[#FF4D00]/10 to-[#1A1A1A] border border-[#FF4D00]/30 hover:border-[#FF4D00]/60 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs font-mono px-1.5 py-0.5 bg-[#FF4D00]/20 text-[#FF4D00] border border-[#FF4D00]/30">
          <Award className="w-3 h-3 inline -mt-0.5 mr-0.5" /> FEATURED
        </span>
        {item.avg_rating > 0 && (
          <span className="text-xs text-[#F59E0B] flex items-center gap-0.5">
            <Star className="w-3 h-3" /> {item.avg_rating?.toFixed(1)}
          </span>
        )}
      </div>
      <h3 className="text-sm font-semibold font-['Barlow_Condensed'] mb-1 truncate">{item.name}</h3>
      <p className="text-xs text-[#A0A0A0] line-clamp-2 mb-2">{item.description}</p>
      <div className="flex items-center gap-3 text-[10px] font-mono text-[#555]">
        {item.use_count > 0 && <span><Users className="w-3 h-3 inline -mt-0.5" /> {item.use_count}</span>}
        {item.fork_count > 0 && <span><GitFork className="w-3 h-3 inline -mt-0.5" /> {item.fork_count}</span>}
      </div>
    </Link>
  );
}

function ItemCard({ item, type }) {
  const href = type === 'personas' ? `/voices` : `/playbooks/${item.slug}`;
  const categoryLabel = item.domain || item.category || '';

  return (
    <Link
      to={href}
      className="block p-4 bg-[#1A1A1A] border border-[#2A2A2A] hover:border-[#FF4D00]/40 transition-colors"
      data-testid={`community-card-${item.slug || item.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold font-['Barlow_Condensed'] truncate">{item.name}</h3>
          {item.author_name && (
            <span className="text-[10px] font-mono text-[#555]">by {item.author_name}</span>
          )}
        </div>
        {item.avg_rating > 0 && (
          <span className="text-xs text-[#F59E0B] flex items-center gap-0.5 shrink-0 ml-2">
            <Star className="w-3 h-3 fill-current" /> {item.avg_rating?.toFixed(1)}
          </span>
        )}
      </div>

      <p className="text-xs text-[#A0A0A0] line-clamp-2 mb-3">{item.description}</p>

      <div className="flex items-center gap-2 flex-wrap">
        {categoryLabel && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[#2A2A2A] text-[#A0A0A0] border border-[#333]">
            {categoryLabel}
          </span>
        )}
        {(item.tags || []).slice(0, 2).map(tag => (
          <span key={tag} className="text-[10px] font-mono px-1.5 py-0.5 bg-[#1A1A1A] text-[#555] border border-[#2A2A2A]">
            {tag}
          </span>
        ))}
      </div>

      <div className="flex items-center gap-4 mt-3 text-[10px] font-mono text-[#555]">
        {item.use_count > 0 && (
          <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {item.use_count} uses</span>
        )}
        {item.fork_count > 0 && (
          <span className="flex items-center gap-1"><GitFork className="w-3 h-3" /> {item.fork_count} forks</span>
        )}
        {item.trending_score > 0 && (
          <span className="flex items-center gap-1 text-[#22C55E]"><TrendingUp className="w-3 h-3" /> trending</span>
        )}
      </div>
    </Link>
  );
}
